const utils = require("../utils");
const getAutocompleteService = require("./autocompleteservice").getAutocompleteService;

/**
 * @typedef {Object} formattedGetDetailsResult
 * formatted map agnostic getDetails result
 * @property {string} [name] name
 * @property {string} [labelAddress] formatted address
 * @property {woosmap.maps.geometry.LatLng} [location] point
 * @property {google.maps.places.PlaceResult|BMap.LocalResult} [rawResult] data retreived by called service (Google/Baidu)
 * @property {Object} [viewpoint] viewpoint
 * @private
 */

/**
 * Formats map getDetails results
 * @param rawResult {google.maps.places.PlaceResult|BMap.LocalResult}
 * @param name {string} name of the result
 * @param labelAddress {string} formatted address for label
 * @param lat {number} latitude
 * @param lng {number} longitude
 * @param viewport {Object|null} viewport
 * @returns {formattedGetDetailsResult}
 * @private
 */
function _formatGetDetailsResult(rawResult, name, labelAddress, lat, lng, viewport = null) {
    let viewpoint;
    if (viewport !== null) {
        const northEast = viewport.getNorthEast();
        const southWest = viewport.getSouthWest();
        viewpoint = {
            bounds: {
                north: northEast.lat(),
                east: northEast.lng(),
                south: southWest.lat(),
                west: southWest.lng(),
            },
        };
    } else {
        viewpoint = {
            point: { longitude: lng, latitude: lat },
            minZoom: 16,
        };
    }

    return Object.assign(rawResult, {
        name,
        formatted_address: labelAddress,
        location: { lat: lat, lng: lng },
        geometry: { location: { lat: () => lat, lng: () => lng } },
        viewpoint,
    });
}

/**
 * @abstract
 * @private
 */
class PlacesInterface {
    /**
     *
     * @param parent
     */
    constructor(parent) {
        this.parent = parent;
        /**
         * Autocomplete service
         * @type {BaiduAutocompleteService|google.maps.places.AutocompleteService}
         * @private
         */
        this._autocompleteImplementation = getAutocompleteService();
    }

    /**
     * @private
     */
    _getSessionToken() {}

    /**
     * @abstract
     */
    getDetails() {}

    /**
     * Use the google Places AutoCompleteService to fetch predictions.
     * @param {String} autocomplete_query string used to search for
     */
    search(autocomplete_query) {
        const request = this.parent.get("options");
        request.input = autocomplete_query;
        request.offset = autocomplete_query.length;
        request.sessionToken = this._getSessionToken();

        if (this._autocompleteImplementation === null) {
            throw new Error(
                "Places library was not loaded, if you need it add 'places' to librariesToLoad parameter in" +
                    " MapsLoader load method.",
            );
        }

        this._autocompleteImplementation.getPlacePredictions(request, (predictions, status) => {
            if (status === "OK") {
                this.parent.set("predictions", predictions);
            } else if (status !== "ZERO_RESULTS") {
                if (!(typeof console === "undefined" || typeof console.error === "undefined")) {
                    console.error(`Places autocomplete was not successful for the following reason: ${status}`);
                }
            }
        });
    }
}

/**
 * Google places implementation.
 * @private
 */
class GooglePlacesImplementation extends PlacesInterface {
    /**
     * Constructor
     * @param parent
     */
    constructor(parent) {
        super(parent);
        /**
         * Places service
         * @type {google.maps.places.PlacesService}
         * @private
         */
        if (google.maps.places !== undefined) {
            this._placesService = new google.maps.places.PlacesService(document.createElement("div"));
        } else {
            this._placesService = null;
        }

        /**
         * Autocomplete session token
         * @type {google.maps.places.AutocompleteSessionToken}
         * @private
         */
        this._sessionToken = null;
    }

    /**
     * Gets internal autocomplete session token
     * @returns {google.maps.places.AutocompleteSessionToken}
     * @private
     */
    _getSessionToken() {
        if (!this._sessionToken) {
            this._sessionToken = new google.maps.places.AutocompleteSessionToken();
        }

        return this._sessionToken;
    }

    /**
     * @typedef woosmapGooglePlacesGetDetailsRequest {Object}
     * @property {string} reference reference
     * @property {string} sessionToken sessionToken
     * @private
     */

    /**
     * Gets the precise location of place result.
     * @param {woosmapGooglePlacesGetDetailsRequest} request reference of the Place
     * @param {function} callback function that will be executed after retrieving places details.
     */
    getDetails(request, callback) {
        const requestData = {
            placeId: request.reference,
            sessionToken: request.sessionToken,
            fields: ["geometry", "name", "formatted_address"],
        };

        if (!requestData.sessionToken) {
            requestData.sessionToken = this._getSessionToken();
        }

        /**
         * getDetails callback
         * @param result {google.maps.places.PlaceResult} getDetails result
         * @param status {google.maps.places.PlacesServiceStatus} getDetails status
         * @private
         */
        const _getDetailsCallback = (result, status) => {
            this._sessionToken = null;
            if (status === google.maps.places.PlacesServiceStatus.OK) {
                const name = result.name;
                const address = result.formatted_address;
                const lat = result.geometry.location.lat();
                const lng = result.geometry.location.lng();

                callback(_formatGetDetailsResult(result, name, address, lat, lng, result.geometry.viewport), status);
            }
        };

        this._placesService.getDetails(requestData, _getDetailsCallback);
    }
}

/**
 * @private
 * @param parent
 * @returns {PlacesInterface}
 */
function getPlacesImplementation(parent) {
    return new GooglePlacesImplementation(parent);
}

module.exports = getPlacesImplementation;
